import * as THREE from "three";
import { assetUrl } from "../assetVer.js";
import { U } from "./streetScene.js";
import { kaponHeightAt } from "./kaponTerrain.js";
import { dayNight } from "./dayNight.js";
import { WEED_STAGES, type WeedPlant } from "@ghostland/shared";

/**
 * Renders the player-planted WEED PLANTS growing in the Kapon wilds: a base-anchored billboard
 * whose texture/scale swaps with the synced growth stage (4 stages + a withered "rotten" look).
 * Mirrors ChestController — position/stage come from the synced `WeedPlant` schema; this
 * controller just spawns/updates/removes sprites (interaction lives in world3d's E ladder).
 */

const SCENE = "/assets/scene/kapon";
/** World-unit sprite heights per stage 1..4 (art: 48/64/80/96px canvases). */
const STAGE_SCALE = [0.43, 0.51, 0.83, 0.87];
const ROTTEN_SCALE = 0.6;

function pixTex(path: string): THREE.Texture {
  const t = new THREE.TextureLoader().load(assetUrl(path));
  t.magFilter = THREE.NearestFilter; t.minFilter = THREE.NearestFilter; t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

interface PlantEntity { x: number; y: number; sprite: THREE.Sprite; stage: number; rotten: boolean; }

export class WeedPlantController {
  private readonly plants = new Map<string, PlantEntity>();
  private readonly stageTex: THREE.Texture[];
  private readonly rottenTex: THREE.Texture;
  private visible = true;

  constructor(private readonly scene: THREE.Scene) {
    this.stageTex = Array.from({ length: WEED_STAGES }, (_, i) => pixTex(`${SCENE}/weed_stage${i + 1}.png`));
    this.rottenTex = pixTex(`${SCENE}/weed_rotten.png`);
  }

  add(key: string, plant: WeedPlant): void {
    if (this.plants.has(key)) return;
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: this.stageTex[0], transparent: true, alphaTest: 0.4 }));
    sprite.center.set(0.5, 0.02); // base-anchored so the soil mound sits on the ground
    const wx = plant.x * U, wz = plant.y * U;
    sprite.position.set(wx, kaponHeightAt(wx, wz) + 0.02, wz);
    sprite.visible = this.visible;
    dayNight.registerMat(sprite.material, sprite.position); // tint with the day/night cycle, like the trees
    this.scene.add(sprite);
    this.plants.set(key, { x: plant.x, y: plant.y, sprite, stage: 0, rotten: false });
    this.update(key, plant);
  }

  /** Re-skin a plant's sprite after a synced change (stage advanced / it rotted). */
  update(key: string, plant: WeedPlant): void {
    const e = this.plants.get(key); if (!e) return;
    const rotten = plant.status === "rotten";
    const stage = Math.max(1, Math.min(WEED_STAGES, plant.stage));
    if (e.stage === stage && e.rotten === rotten) return;
    e.stage = stage; e.rotten = rotten;
    const mat = e.sprite.material as THREE.SpriteMaterial;
    mat.map = rotten ? this.rottenTex : this.stageTex[stage - 1];
    mat.needsUpdate = true;
    const s = rotten ? ROTTEN_SCALE : STAGE_SCALE[stage - 1];
    e.sprite.scale.set(s, s, 1);
  }

  remove(key: string): void {
    const e = this.plants.get(key); if (!e) return;
    this.scene.remove(e.sprite);
    dayNight.unregisterMat(e.sprite.material);
    (e.sprite.material as THREE.SpriteMaterial).dispose(); // textures are shared — only the material goes
    this.plants.delete(key);
  }

  setVisible(v: boolean): void {
    if (v === this.visible) return;
    this.visible = v;
    for (const e of this.plants.values()) e.sprite.visible = v;
  }

  /** Nearest plant within `range` server px of (px,py), or null. Drives the E prompt + panel. */
  nearest(px: number, py: number, range: number): { key: string; x: number; y: number } | null {
    let best = range * range, found: { key: string; x: number; y: number } | null = null;
    for (const [key, e] of this.plants) {
      const dx = e.x - px, dy = e.y - py, d2 = dx * dx + dy * dy;
      if (d2 <= best) { best = d2; found = { key, x: e.x, y: e.y }; }
    }
    return found;
  }

  dispose(): void {
    for (const key of [...this.plants.keys()]) this.remove(key);
    for (const t of this.stageTex) t.dispose();
    this.rottenTex.dispose();
  }
}
